const TARGET_TIME_ZONE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/

function hasValidCalendarParts(match) {
  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue = '0'] =
    match
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const hour = Number(hourValue)
  const minute = Number(minuteValue)
  const second = Number(secondValue)
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysByMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysByMonth[month - 1] &&
    hour <= 23 &&
    minute <= 59 &&
    second <= 59
  )
}

export function parseTargetTimestamp(value) {
  const normalizedValue = String(value || '').trim()

  const match = TARGET_TIME_ZONE_PATTERN.exec(normalizedValue)

  if (!match || !hasValidCalendarParts(match)) return null

  const timestamp = Date.parse(normalizedValue)
  return Number.isFinite(timestamp) ? timestamp : null
}

export function getRemainingParts(targetTimestamp, now = Date.now()) {
  const remainingMilliseconds = Math.max(targetTimestamp - now, 0)
  const totalSeconds = Math.floor(remainingMilliseconds / 1000)

  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
    expired: remainingMilliseconds <= 0,
  }
}

const BaseHTMLElement = globalThis.HTMLElement || class {}

export class AnniversaryCountdown extends BaseHTMLElement {
  connectedCallback() {
    this.abortController?.abort()
    this.abortController = new AbortController()
    this.targetTimestamp = parseTargetTimestamp(this.dataset.targetTime)
    this.values = {
      days: this.querySelector('[data-countdown-value="days"]'),
      hours: this.querySelector('[data-countdown-value="hours"]'),
      minutes: this.querySelector('[data-countdown-value="minutes"]'),
      seconds: this.querySelector('[data-countdown-value="seconds"]'),
    }
    this.units = {
      days: this.querySelector('[data-countdown-unit="days"]'),
      hours: this.querySelector('[data-countdown-unit="hours"]'),
      minutes: this.querySelector('[data-countdown-unit="minutes"]'),
      seconds: this.querySelector('[data-countdown-unit="seconds"]'),
    }
    this.accessibleOutput = this.querySelector('[data-countdown-accessible]')

    document.addEventListener('visibilitychange', () => this.handleVisibilityChange(), {
      signal: this.abortController.signal,
    })

    if (this.targetTimestamp === null) {
      this.hidden = true
      return
    }

    this.tick()
  }

  disconnectedCallback() {
    this.abortController?.abort()
    this.abortController = null
    this.clearTickTimer()
  }

  clearTickTimer() {
    if (!this.tickTimer) return

    clearTimeout(this.tickTimer)
    this.tickTimer = null
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.clearTickTimer()
      return
    }

    this.tick()
  }

  tick() {
    this.clearTickTimer()

    const now = Date.now()
    const parts = getRemainingParts(this.targetTimestamp, now)

    if (parts.expired) {
      this.hidden = true
      return
    }

    this.hidden = false

    Object.entries(this.values).forEach(([key, element]) => {
      if (element) element.textContent = String(parts[key]).padStart(2, '0')
    })

    if (this.accessibleOutput) {
      this.accessibleOutput.textContent = Object.entries(this.units)
        .map(([key, element]) => `${parts[key]} ${element?.textContent?.trim() || ''}`.trim())
        .join(', ')
    }

    if (!document.hidden) {
      const nextSecondDelay = Math.max(1000 - (now % 1000) + 20, 20)
      this.tickTimer = setTimeout(() => this.tick(), nextSecondDelay)
    }
  }
}

if (globalThis.customElements && !globalThis.customElements.get('anniversary-countdown')) {
  globalThis.customElements.define('anniversary-countdown', AnniversaryCountdown)
}
